/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { request } from '@mairistem/tools';

import { BASE_URL_ETATCIVIL } from '@/constants';
import type { Registre } from '@/types';

/**
 * Nœud de l'arbre renvoyé par `GET /registres/listeByType`.
 *
 * ⚠️ L'endpoint ne renvoie pas une liste plate de registres mais un arbre
 * groupé par année : `[{ id: '2025', label: '2025', children: [Registre] }, …]`.
 * `ModalRegistreTreeView` type sa réponse `Registre[]`, ce qui est inexact — le
 * composant ne fait que la passer au `RegistreTreeView` de la lib, qui attend
 * bien l'arbre.
 */
interface NoeudAnnee {
  id?: string | number;
  label?: string | number;
  children?: Registre[];
}

/**
 * Registre à présélectionner : celui de l'**année en cours** s'il existe, sinon
 * le plus **récent** disponible.
 *
 * Le repli n'est pas un cas d'école : les registres sont créés par la commune,
 * et sur la base de référence le plus récent est 2025 alors que l'année en cours
 * est 2026 — l'année courante manque donc le plus souvent en début d'exercice.
 *
 * L'année maximale est calculée explicitement, sans se reposer sur l'ordre de
 * l'API (aujourd'hui décroissant, mais ce n'est pas un contrat).
 *
 * @param arbre  Arbre renvoyé par l'endpoint.
 * @return Le registre retenu, ou `undefined` si l'arbre n'en contient aucun.
 */
export const choisirRegistreParDefaut = (
  arbre: NoeudAnnee[] | undefined,
): Registre | undefined => {
  const anneeDe = (noeud: NoeudAnnee) => Number(noeud?.label ?? noeud?.id);

  // Un nœud sans registre exploitable ne peut pas être présélectionné.
  const exploitables = _.filter(
    arbre,
    (noeud) => _.isFinite(anneeDe(noeud)) && !_.isEmpty(noeud?.children),
  );

  if (_.isEmpty(exploitables)) {
    return undefined;
  }

  const anneeCourante = new Date().getFullYear();
  const retenu =
    _.find(exploitables, (noeud) => anneeDe(noeud) === anneeCourante) ??
    _.maxBy(exploitables, anneeDe);

  return retenu?.children?.[0];
};

/**
 * Charge l'arbre des registres du type donné et en déduit celui à
 * présélectionner dans l'assistant (cf. `choisirRegistreParDefaut`).
 *
 * `actif` évite l'appel réseau quand il n'y a rien à préremplir — un registre
 * est déjà choisi, ou le type d'acte n'est pas encore connu.
 *
 * Distinct de `ModalRegistreTreeView`, qui ne charge la liste qu'à l'**ouverture**
 * de la modale : le préremplissage doit, lui, avoir lieu avant toute ouverture.
 */
const useRegistreParDefaut = (
  typeRegistre: string | undefined,
  actif = true,
) => {
  const [registre, setRegistre] = React.useState<Registre>();

  React.useEffect(() => {
    if (!actif || _.isEmpty(typeRegistre)) {
      return;
    }

    let abandonne = false;
    const params = new URLSearchParams({
      typeRegistre: typeRegistre as string,
    });

    request
      .get<unknown, NoeudAnnee[]>(
        `${BASE_URL_ETATCIVIL}/registres/listeByType?${params}`,
      )
      .then((reponse: NoeudAnnee[]) => {
        if (!abandonne) {
          setRegistre(choisirRegistreParDefaut(reponse));
        }
      })
      // Silencieux à dessein : l'absence de préremplissage n'est pas une erreur
      // pour l'agent, qui garde la sélection manuelle.
      .catch(() => undefined);

    return () => {
      abandonne = true;
    };
  }, [typeRegistre, actif]);

  return registre;
};

export default useRegistreParDefaut;
